/**
 * Live updates: subscribes to GET /api/v2/events (Server-Sent Events) and
 * invalidates the affected queries; pages that need it also poll (jobs and
 * operations every 3 s while running). A dropped stream (a deploy, a phone
 * switching networks, a laptop waking up) is reopened with a capped backoff
 * and never given up — a voice call's transcript and hang-up arrive here —
 * and right away when the browser comes back online or the tab is shown again.
 */

import type { QueryClient } from "@tanstack/vue-query";
import { ref } from "vue";
import { toast } from "vue-sonner";
import { dispatchSchorschiEvent, type SchorschiEvent } from "./schorschi";

export const liveConnected = ref(false);

let source: EventSource | null = null;
let failures = 0;
let retry: ReturnType<typeof setTimeout> | null = null;
let started = false;
let listening = false;
/** Reopen now (force: even if the current connection looks open). */
let reconnect: (force: boolean) => void = () => undefined;

/** Hidden longer than this (a locked phone, a sleeping laptop): the stream is reopened when shown again. */
const STALE_AFTER_HIDDEN_MS = 60_000;

/** Wait before reconnect attempt `n` (1-based): 1, 2, 5, 10, 20, then 30 s, with ±20 % jitter so replicas aren't stampeded after a deploy. */
export function reconnectDelay(n: number, random: () => number = Math.random): number {
  const steps = [1_000, 2_000, 5_000, 10_000, 20_000, 30_000];
  const base = steps[Math.min(Math.max(n, 1), steps.length) - 1];
  return Math.round(base * (0.8 + 0.4 * random()));
}

/** Stop the stream (tests; signing out reloads the page). */
export function stopLiveEvents(): void {
  if (retry) clearTimeout(retry);
  retry = null;
  source?.close();
  source = null;
  failures = 0;
  started = false;
  liveConnected.value = false;
}

export function startLiveEvents(queryClient: QueryClient): void {
  if (started || typeof EventSource === "undefined") return;
  started = true;
  const connect = () => {
    retry = null;
    if (source) return;
    source = new EventSource("/api/v2/events", { withCredentials: true });
    source.onopen = () => {
      failures = 0;
      liveConnected.value = true;
    };
    const invalidate = (keys: string[][]) => () => keys.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }));
    source.addEventListener("job", invalidate([["jobs"], ["actions"]]));
    // Action runs are operations; their step progress arrives as operation events too.
    source.addEventListener("operation", invalidate([["operations"], ["jobs"], ["actions"]]));
    source.addEventListener("authz", invalidate([["me"]]));
    // Agent runs (status and transcript steps) and todo lists (docs/v2/agents.md).
    source.addEventListener("agent", invalidate([["agents"]]));
    source.addEventListener("todos", invalidate([["todos"]]));
    source.addEventListener("knowledge", invalidate([["knowledge"]]));
    // The inbox (Schorschi's messages and replies, finished agent runs, todo assignments).
    source.addEventListener("notification", (e) => {
      invalidate([["notifications"]])();
      try {
        const n = JSON.parse((e as MessageEvent).data) as { id: string; source: string; kind: string; title: string; link: string | null };
        // A person's own investigation is announced by the schorschi event below.
        if (n.kind === "research") return;
        const show = n.kind === "error" ? toast.error : toast.info;
        const link = n.link;
        show(n.title, { id: `notification-${n.id}`, duration: 10_000, ...(link ? { action: { label: "Open", onClick: () => (link.startsWith("/") ? window.location.assign(link) : window.open(link, "_blank", "noopener")) } } : {}) });
      } catch {
        // malformed payload: the list refresh is enough
      }
    });
    // An own connection changed (e.g. needs_reauth after a token was rejected upstream).
    source.addEventListener("connection", invalidate([["connections"]]));
    // AI budget warning / limit reached (once per period): show it and refresh the budget card.
    source.addEventListener("budget", (e) => {
      invalidate([["me", "ai-budget"], ["admin", "ai"]])();
      try {
        const data = JSON.parse((e as MessageEvent).data) as { level?: string; message?: string; policyId?: string; mine?: boolean };
        if (!data.message) return;
        const show = data.level === "exceeded" ? toast.error : toast.warning;
        show(data.mine === false ? `Budget: ${data.message}` : data.message, { id: `budget-${data.policyId}-${data.level}`, duration: 15_000 });
      } catch {
        // malformed payload: nothing to show
      }
    });
    // Schorschi: refresh what changed for everyone (mood, feed), hand the rest to the page (avatar, chat, calls).
    source.addEventListener("schorschi", (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data) as SchorschiEvent;
        if (data.kind === "mood") invalidate([["schorschi", "status"]])();
        if (data.kind === "journal") invalidate([["schorschi", "feed"], ["schorschi", "picture"], ["schorschi", "proposals"]])();
        if (data.kind === "activity" && data.researchDone) {
          invalidate([["schorschi", "feed"], ["schorschi", "proposals"]])();
          toast.info(`Schorschi finished looking into it: ${data.researchDone.title}`, { duration: 12_000, action: { label: "Open", onClick: () => window.location.assign("/schorschi?tab=feed") } });
        }
        dispatchSchorschiEvent(data);
      } catch {
        // malformed payload: nothing to do
      }
    });
    source.onerror = () => {
      liveConnected.value = false;
      source?.close();
      source = null;
      failures++;
      // Polling keeps the pages going meanwhile; the stream comes back on its own.
      if (started && !retry) retry = setTimeout(connect, reconnectDelay(failures));
    };
  };
  reconnect = (force) => {
    if (!started) return;
    if (source && !force) return;
    if (retry) clearTimeout(retry);
    source?.close();
    source = null;
    connect();
  };
  if (!listening && typeof window !== "undefined") {
    listening = true;
    // Back online, or shown again (a phone unlocked, a laptop opened): don't wait for the backoff.
    // After a long sleep the old connection may look open but be dead (heartbeats are comments the
    // browser never reports), so it is replaced.
    let hiddenAt: number | null = null;
    window.addEventListener("online", () => reconnect(false));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") hiddenAt = Date.now();
      else {
        reconnect(hiddenAt !== null && Date.now() - hiddenAt > STALE_AFTER_HIDDEN_MS);
        hiddenAt = null;
      }
    });
  }
  connect();
}
